import {
  describeLocator,
  type EvidenceCitation,
  type FindingStatement,
  type GroundClause,
  slotOf,
} from '@zevik/schemas';

/**
 * What a finding is grounded on, and where each citation points
 * (docs/DECISIONS.md ZD-112).
 *
 * ## The three-valued rules are carried across explicitly, not inherited
 *
 * CLAUDE.md's rule is that a rule established at one layer does not travel to the
 * next by being true, and the specific defect it names happened in the report's
 * prose: *"no observed control applies"* rendered over a coverage that is
 * **undeterminate** on 32 of 85 corpus subjects. So each slot here is read on its
 * own three-valued terms and each one is said where it lands:
 *
 * - `grounds` — `established` means these clauses are the witnesses;
 *   `unestablished` means the pattern applied structurally and consulted no
 *   attribute; `not_applicable` means the verdict was `unknown`, where there
 *   cannot be a witness by construction. Three different sentences.
 * - `observed_controls` — `coverage` is the only field carrying a claim, and
 *   `null` says *we could not tell*, never *nothing protects it*.
 * - `impacted_assets` — `unestablished` says no asset is linked, which is the
 *   commonest case and is what the band was computed without.
 *
 * ## Ids, and why they are shown rather than resolved here
 *
 * `renderStatement` names every object in words at store time, so the prose above
 * this block already reads *"the flow Booking API → Payments"*. The ids in this
 * block are the same objects in the form a decision anchors on, shown so a
 * reviewer can tell two findings on similar objects apart. Resolving them to names
 * in the browser would need the canonical model, which no route returns — and
 * ZD-111 §3 records that absence as deliberate for exactly this reason.
 *
 * `control_direction` is **not rendered**. It carries the catalogue's own
 * sentences, whose per-field audience is unsettled, and it is `SLOT_NOT_RENDERED`
 * in the report for a related reason. The workspace says so under *what this
 * screen cannot do* rather than showing it.
 */

function Citations({ citations }: { citations: readonly EvidenceCitation[] }): React.JSX.Element {
  if (citations.length === 0) {
    return (
      <p className="absent">
        Nothing was cited for this — the fact was established without a document reference.
      </p>
    );
  }
  return (
    <ul className="citations">
      {citations.map((citation, index) => (
        <li key={`${citation.ref}-${String(index)}`}>
          <span className="ref">{citation.ref}</span>
          <span className="where">{describeLocator(citation.locator)}</span>
        </li>
      ))}
    </ul>
  );
}

function Clause({ clause }: { clause: GroundClause }): React.JSX.Element {
  return (
    <div className="clause">
      <p className="clause__fact">
        <span className="clause__attribute">{clause.attribute}</span>
        {' of '}
        <span className="clause__object">{clause.object_id}</span>
        {/*
         * A literal space, not only the chip's margin. The margin separates it on
         * screen and leaves `component_d7way…labelled` in the copied text and in
         * what a screen reader announces — which is what reading the rendered
         * output found, and what no assertion about the markup would have.
         */}
        {clause.basis === null ? null : (
          <>
            {' '}
            <span className="clause__basis">{clause.basis}</span>
          </>
        )}
      </p>
      <Citations citations={clause.citations} />
    </div>
  );
}

function Ids({ ids }: { ids: readonly string[] }): React.JSX.Element {
  return (
    <ul className="idlist">
      {ids.map((id) => (
        <li key={id}>{id}</li>
      ))}
    </ul>
  );
}

export function Grounding({ statement }: { statement: FindingStatement }): React.JSX.Element {
  const grounds = slotOf(statement, 'grounds');
  const undecided = slotOf(statement, 'undecided');
  const boundary = slotOf(statement, 'crossed_boundary');
  const assets = slotOf(statement, 'impacted_assets');
  const controls = slotOf(statement, 'observed_controls');
  const subject = slotOf(statement, 'subject');

  return (
    <>
      <section className="block">
        <h3>What this rests on</h3>
        {grounds.state === 'established' ? (
          grounds.clauses.map((clause, index) => (
            <Clause
              key={`${clause.object_id}-${clause.attribute}-${String(index)}`}
              clause={clause}
            />
          ))
        ) : grounds.state === 'unestablished' ? (
          <p className="absent">
            The pattern applies to this object structurally. No attribute of it was consulted, so
            there is no witness to check — which is a fact about this system rather than a gap.
          </p>
        ) : (
          <p className="absent">
            There are no witnesses here, and that is structural rather than missing: this pattern
            could not be decided on the model as it stands, and an undecided verdict has nothing to
            rest on by construction. What it is waiting on is below.
          </p>
        )}
      </section>

      {undecided.state === 'established' ? (
        <section className="block">
          <h3>What could not be decided</h3>
          <p className="absent">
            These were never established, so the model cannot say either way. Answering them
            confirms or withdraws this finding.
          </p>
          <ul className="idlist">
            {undecided.refs.map((ref) => (
              <li key={`${ref.object_id}|${ref.attribute}`}>
                {ref.attribute} of {ref.object_id}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <section className="block">
        <h3>What was observed protecting it</h3>
        {controls.state === 'not_applicable' ? (
          <p className="absent">
            Not assessed for this finding: the pattern does not ask about observed controls.
          </p>
        ) : controls.coverage === 'covered' ? (
          <>
            <p className="absent">
              Observed controls of kind {controls.kinds_sought.join(', ')} apply to it.
            </p>
            <Ids ids={controls.covering} />
          </>
        ) : controls.coverage === 'none_observed' ? (
          <p className="absent">
            No observed control of kind {controls.kinds_sought.join(', ')} applies to it, and{' '}
            {controls.examined.length === 0
              ? 'the model records none at all.'
              : `${String(controls.examined.length)} were examined.`}
          </p>
        ) : (
          <p className="absent">
            <strong>We could not tell.</strong> Whether an observed control of kind{' '}
            {controls.kinds_sought.join(', ')} applies here was not established — which is not the
            same as nothing protecting it, and must not be read that way.
          </p>
        )}
        {controls.examined.length > 0 ? (
          <div>
            {controls.examined.map((clause, index) => (
              <Clause key={`examined-${clause.object_id}-${String(index)}`} clause={clause} />
            ))}
          </div>
        ) : null}
      </section>

      <section className="block">
        <h3>What it affects</h3>
        {assets.state === 'established' ? (
          <Ids ids={assets.asset_ids} />
        ) : (
          <p className="absent">
            Not established: no asset is linked to this object. The severity band was worked out
            without it, and says so.
          </p>
        )}
      </section>

      {/*
       * **An id is data here and never a sentence, which reading the output is
       * what settled.** The first version read *"The subject is
       * component_d7way7s2387xc0knfmcpsk."* — an opaque id inside prose a
       * reviewer is meant to act on, which is `sds-05a`'s recorded defect
       * exactly, and redundant besides: the finding's own title already names
       * the object in words. The ids belong on the screen so two findings about
       * similar objects can be told apart, and they belong as chips.
       *
       * The heading changed with it. *"Objects this decision anchors on"* was
       * wrong twice over: a decision anchors on `finding_key` and
       * `finding_structural_key`, neither of which is here, and a crossed
       * boundary is not something a decision anchors on at all.
       */}
      <section className="block">
        <h3>The objects behind this, by id</h3>
        <p className="absent">
          Shown because the sentences above name these objects the way your documents do, and two
          findings can read alike. The subject:
        </p>
        <Ids ids={[subject.object_id]} />
        {boundary.state === 'established' ? (
          <>
            <p className="absent">
              The {String(boundary.boundary_ids.length)} trust{' '}
              {boundary.boundary_ids.length === 1 ? 'boundary' : 'boundaries'} it crosses:
            </p>
            <Ids ids={boundary.boundary_ids} />
          </>
        ) : null}
      </section>
    </>
  );
}
